// React core components
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// @mui Material components
import {
  Grid,
  Typography,
  TextField,
  Icon,
  Snackbar,
  Alert,
  Modal,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

// Material Dashboard 2 PRO React components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Services
import crudsService from "services/cruds-service";
import BasicInformationCard from "./basic-information";
import HomepageIntroduction from "./homepage-introduction";
import Modules from "./modules";

const CourseForm = () => {
  // State Declarations
  const [imageModalOpenState, setImageModalOpenState] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [coursesOptions, setCoursesOptions] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [selectedCoursesToDelete, setSelectedCoursesToDelete] = useState([]);
  const [isDeletingCourses, setIsDeletingCourses] = useState(false);

  const navigate = useNavigate();

  // Course information including details and configuration
  const [courseInformation, setCourseInformation] = useState({
    name: "",
    image_location: "",
    disclaimer: "",
    home_page_introduction: "",
    podcastLink: "",
    previewVideoLink: "",
    previewSlideDeckLink: "",
    short_description: "",
    retriever_db_path: "",
    hybrid_db_path: "",
    questions_file: "",
    external_link: "",
    payment_link: "",
    certificate_path: "",
    has_chatbot: false,
    has_quiz: false,
    chatbot_location: "",
  });

  // Notification state for alerts
  const [notification, setNotification] = useState({
    value: false,
    color: "info",
    message: "",
  });

  // Home page introduction text
  const [homePageIntroduction, setHomePageIntroduction] = useState("# Introduction");
  const [podcastLink, setPodcastLink] = useState("");
  const [previewVideoLink, setPreviewVideoLink] = useState("");
  const [previewSlideDeckLink, setPreviewSlideDeckLink] = useState("");

  // Initial module state (simplified for this section)
  const [modules, setModules] = useState([]);

  // Error tracking state for inputs
  const [errors, setErrors] = useState({
    app_name_error: false,
    app_image_location_error: false,
    short_description_error: false,
    no_modules_error: false,
    no_questions_error: false,
    wrong_format_error: false,
    no_answer_error: false,
    invalid_answer_option_error: false,
    no_explanation_error: false,
    no_options_error: false,
  });

  // Effects
  const fetchCoursesOptions = async () => {
    try {
      const response = await crudsService.getCourseList();
      if (response) {
        setCoursesOptions(response);
      }
    } catch (error) {
      console.log("Error", error);
      setNotification({
        value: true,
        color: "error",
        message: "Failed to fetch courses. Please try again later.",
      });
    }
  };

  // Fetch related courses options on mount
  useEffect(() => {
    fetchCoursesOptions();
  }, []);

  // Auto-close notification after 5 seconds
  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({ value: false, color: "info", message: "" });
      }, 5000);
    }
  }, [notification]);

  // Event Handlers & Functions

  // Handle input changes for course information
  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setCourseInformation({ ...courseInformation, [name]: value });
    setErrors({ ...errors, [`${name}_error`]: false });
  };

  const handleDeleteCourses = async () => {
    if (selectedCoursesToDelete.length === 0) {
      setNotification({
        value: true,
        color: "error",
        message: "Please select at least one course to delete.",
      });
      return;
    }

    setIsDeletingCourses(true);
    try {
      await Promise.all(selectedCoursesToDelete.map((course) => crudsService.deleteCourse(course._id)));

      if (courseId && selectedCoursesToDelete.some((course) => course._id === courseId)) {
        resetCourse();
      }

      setNotification({
        value: true,
        color: "success",
        message: `${selectedCoursesToDelete.length} course(s) deleted successfully.`,
      });

      setSelectedCoursesToDelete([]);
      setConfirmDeleteModal(false);
      setDeleteModal(false);
      await fetchCoursesOptions();
    } catch (error) {
      console.log("Error", error);
      setNotification({
        value: true,
        color: "error",
        message: "Failed to delete selected course(s). Please try again later.",
      });
    } finally {
      setIsDeletingCourses(false);
    }
  };

  // Module Management Functions





  // Function to handle the form submission
  const submitHandler = async () => {
    // Validate that all modules have a non-blank name
    for (let i = 0; i < modules.length; i++) {
      if (modules[i].name === "") {
        setNotification({
          value: true,
          color: "error",
          message: "Module name cannot be blank.",
        });
        return;
      }
    }

    if (courseInformation.has_quiz) {
      if (courseInformation.questions_file === "" || courseInformation.certificate_path === "") {
        setNotification({
          value: true,
          color: "error",
          message: "Questions file (.json) and certificate path (.jpg) are required for courses with quizzes.",
        });
        return;
      }
    }
    else {
      setCourseInformation({
        ...courseInformation,
        questions_file: "",
        certificate_path: "",
      })
    }



    // Construct the course object payload
    let course = {
      app_name: courseInformation.name,
      app_image_location: courseInformation.image_location,
      home_page_introduction: homePageIntroduction,
      podcastLink: podcastLink,
      previewVideoLink: previewVideoLink,
      previewSlideDeckLink: previewSlideDeckLink,
      short_description: courseInformation.short_description,
      external_link: courseInformation.external_link,
      disclaimer: courseInformation.disclaimer,
      has_chatbot: courseInformation.has_chatbot || false,
      has_quiz: courseInformation.has_quiz || false,
      external_link: courseInformation.external_link || "",
      payment_link: courseInformation.payment_link || "",
      questions_file: courseInformation.questions_file || "",
      certificate_path: courseInformation.certificate_path || "",
      modules: modules
    };

    // If editing an existing course, call editCourse API; otherwise, create a new course
    if (editCourse) {
      try {
        const response = await crudsService.editCourse(course, courseId);
        setNotification({
          value: true,
          color: "success",
          message: "Course updated successfully.",
        });
      } catch (error) {
        console.log("Error", error);
        setNotification({
          value: true,
          color: "error",
          message: "Failed to update course. Please try again later.",
        });
      }
    } else {
      try {
        const response = await crudsService.createCourse(course);
        const newCourseId = response.data._id || response.data.id;
        await crudsService.editCourse(course, newCourseId);
        setNotification({
          value: true,
          color: "success",
          message: "Course added successfully.",
        });
      } catch (error) {
        console.log("Error", error);
        setNotification({
          value: true,
          color: "error",
          message: "Failed to add course. Please try again later.",
        });
      }
    }

    // Redirect to the course catalog page after processing
    navigate("/course-catalog")
  }


  // Steps for the Stepper component
  const steps = ["Basic Information", "Home Page Introduction", "Modules"];

  // Handler for moving to the next step
  const handleNext = () => {
    // Validate required fields on first step
    if (
      activeStep === 0 &&
      (!courseInformation.name ||
        !courseInformation.image_location ||
        !courseInformation.short_description)
    ) {
      setNotification({
        value: true,
        color: "error",
        message: "Please fill in all the required fields.",
      });
      return;
    }
    // Validate home page introduction on second step
    if (activeStep === 1 && homePageIntroduction === "") {
      setNotification({
        value: true,
        color: "error",
        message: "Please fill in the home page introduction.",
      });
      return;
    }
    // Proceed to the next step
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handler for moving to the previous step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Modal and course editing state declarations
  const [editModal, setEditModal] = useState(false);
  const [editCourse, setEditCourse] = useState(false);
  const [courseId, setCourseId] = useState("");

  // Function to fetch and load a course for editing based on its ID
  const handleEditCourse = async (courseId) => {
    // Helper function to fetch course information from the service
    async function fetchCourseInformation() {
      try {
        const response = await crudsService.getCourse(courseId, true);
        return response;
      } catch (error) {
        console.log("Error", error);
        setNotification({
          value: true,
          color: "error",
          message: "Failed to get course information. Please try again later.",
        });
      }
    }
    // Retrieve course data and update state accordingly
    try {
      const response = await fetchCourseInformation();
      // Set basic course information
      let courseInformation = {
        name: response.app_name,
        image_location: response.app_image_location,
        home_page_introduction: response.home_page_introduction,
        podcastLink: response.podcastLink,
        previewVideoLink: response.previewVideoLink,
        previewSlideDeckLink: response.previewSlideDeckLink,
        short_description: response.short_description,
        external_link: response.external_link,
        payment_link: response.payment_link || "",
        disclaimer: response.disclaimer || "",
        has_chatbot: response.has_chatbot || false,
        has_quiz: response.has_quiz || false,
        chatbot_location: response.chatbot_location || "",
        level: response.level || "",
        total_video_minutes: response.total_video_minutes || 0,
        certificate_path: response.certificate_path || "",
        questions_file: response.questions_file || "",
      };
      setCourseInformation(courseInformation);
      setModules(response.modules);

      // Set the home page introduction from the response
      let homePageIntroduction = response.home_page_introduction;
      setHomePageIntroduction(homePageIntroduction);
      setPodcastLink(response.podcastLink);
      setPreviewVideoLink(response.previewVideoLink);
      setPreviewSlideDeckLink(response.previewSlideDeckLink);
    } catch (error) {
      console.log("Error", error);
      setNotification({
        value: true,
        color: "error",
        message: "Failed to get course information. Please try again later.",
      });
    }
  };

  // Function to reset the course form to its initial state
  const resetCourse = () => {
    setCourseInformation({
      name: "",
      image_location: "",
      external_link: "",
      payment_link: "",
      home_page_introduction: "",
      podcastLink: "",
      previewVideoLink: "",
      previewSlideDeckLink: "",
      short_description: "",
      retriever_db_path: "",
      hybrid_db_path: "",
      questions_file: "",
      certificate_path: "",
      has_chatbot: false,
      has_quiz: false,
      chatbot_location: "",
      external_link: "",
      level: "",
      total_video_minutes: 0,
    });

    setHomePageIntroduction("# Introduction");

    setModules([
      {
        module_name: "",
        module_information: "",
        module_slide_link: "",
        module_video: "",
        reference_link: "",
        colab_link: "",
        lab_link: "",
      },
    ]);

    setEditCourse(false);
    setCourseId("");
  };

  const handleCancelUpdate = () => {
    resetCourse();
    setActiveStep(0);
    setNotification({
      value: true,
      color: "info",
      message: "Course update cancelled.",
    });
  };

  return (
    <>
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} display="flex" justifyContent="space-between">
              <Typography variant="h3" >
                Add Course
              </Typography>
              <MDBox display="flex">
                <MDButton variant="gradient" color="info" onClick={resetCourse} style={{
                  marginRight: "10px"
                }}>
                  <Icon style={{ verticalAlign: "middle", marginRight: "5px" }}>rotate_left</Icon>
                  <b>Reset</b>
                </MDButton>
                <MDButton variant="gradient" color="info" onClick={() => {
                  setEditModal(true);
                }}>
                  <Icon style={{ verticalAlign: "middle", marginRight: "5px" }}>edit</Icon>
                  <b>Load Course</b>
                </MDButton>
                <MDButton variant="gradient" color="error" style={{ marginLeft: "10px" }} onClick={() => {
                  fetchCoursesOptions();
                  setSelectedCoursesToDelete([]);
                  setDeleteModal(true);
                }}>
                  <Icon style={{ verticalAlign: "middle", marginRight: "5px" }}>delete</Icon>
                  <b>Delete Course</b>
                </MDButton>
              </MDBox>
            </Grid>
            {
              <Modal open={editModal} onClose={() => {
                setEditModal(false);
              }}>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "8px",
                    outline: "none",
                    minWidth: "50vw",
                  }}
                >
                  <Typography style={{ marginBottom: "10px" }}>Select a course to load it for editing.</Typography>
                  <Autocomplete
                    id="tags-outlined"
                    options={coursesOptions}
                    getOptionLabel={(option) => option.app_name}
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Select course"
                        placeholder="Select course"
                      />
                    )}
                    onChange={(event, value) => {
                      setCourseId(value?._id);
                    }}
                  />
                  <MDBox style={{
                    display: "flex",
                    marginTop: "20px",
                    marginLeft: "auto",
                  }}>
                    <MDButton variant="gradient" color="info"
                      style={{
                        marginRight: "10px"
                      }}
                      onClick={() => {
                        setCourseId("");
                        setEditCourse(false);
                        setEditModal(false);
                      }
                      }>
                      Cancel
                    </MDButton>
                    <MDButton variant="gradient" color="success" onClick={() => {
                      handleEditCourse(courseId);
                      setActiveStep(0);
                      setEditCourse(true);
                      setEditModal(false);
                    }}>
                      Load Course
                    </MDButton>
                  </MDBox>
                </div>
              </Modal>
            }
            {
              <Modal open={deleteModal} onClose={() => {
                if (isDeletingCourses) return;
                setDeleteModal(false);
              }}>
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "8px",
                    outline: "none",
                    minWidth: "50vw",
                  }}
                >
                  <Typography style={{ marginBottom: "10px" }}>Select one or more courses to delete.</Typography>
                  <Autocomplete
                    multiple
                    options={coursesOptions}
                    getOptionLabel={(option) => option.app_name}
                    value={selectedCoursesToDelete}
                    filterSelectedOptions
                    onChange={(event, value) => {
                      setSelectedCoursesToDelete(value || []);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Select courses"
                        placeholder="Select courses"
                      />
                    )}
                  />
                  <MDBox style={{
                    display: "flex",
                    marginTop: "20px",
                    marginLeft: "auto",
                  }}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      style={{ marginRight: "10px" }}
                      onClick={() => {
                        setSelectedCoursesToDelete([]);
                        setDeleteModal(false);
                      }}
                    >
                      Cancel
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="error"
                      disabled={selectedCoursesToDelete.length === 0}
                      onClick={() => {
                        setDeleteModal(false);
                        setConfirmDeleteModal(true);
                      }}
                    >
                      Continue
                    </MDButton>
                  </MDBox>
                </div>
              </Modal>
            }

            <Dialog
              open={confirmDeleteModal}
              onClose={() => {
                if (isDeletingCourses) return;
                setConfirmDeleteModal(false);
              }}
            >
              <DialogTitle>Confirm Course Deletion</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  This action is irreversible and all changes will be lost forever.
                </DialogContentText>
                <DialogContentText>
                  If there are any users currently looking at the selected course(s), they will be disrupted because the course(s) will be deleted.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    setConfirmDeleteModal(false);
                  }}
                  disabled={isDeletingCourses}
                >
                  Cancel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="error"
                  onClick={handleDeleteCourses}
                  disabled={isDeletingCourses}
                >
                  {isDeletingCourses ? "Deleting..." : "Delete Course(s)"}
                </MDButton>
              </DialogActions>
            </Dialog>
            <Grid item xs={12}>
              <Stepper activeStep={activeStep} orientation="horizontal"
                style={{
                  padding: "10px"
                }}>
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>


            {/* Basic Information Card */}
            {activeStep === 0 && <Grid item xs={12}>
              <BasicInformationCard
                courseInformation={courseInformation}
                onChangeHandler={onChangeHandler}
                errors={errors}
                setImageModalOpenState={setImageModalOpenState}
                imageModalOpenState={imageModalOpenState}
                has_quiz={courseInformation.has_quiz}
                questions_file={courseInformation.questions_file}
                certificate_path={courseInformation.certificate_path}
              />
            </Grid>}

            {/* Home Page Introduction */}
            {activeStep === 1 && <Grid item xs={12}>
              <HomepageIntroduction
                homePageIntroduction={homePageIntroduction}
                setHomePageIntroduction={setHomePageIntroduction}
                podcastLink={podcastLink}
                setPodcastLink={setPodcastLink}
                previewVideoLink={previewVideoLink}
                setPreviewVideoLink={setPreviewVideoLink}
                previewSlideDeckLink={previewSlideDeckLink}
                setPreviewSlideDeckLink={setPreviewSlideDeckLink}
                handleChange={onChangeHandler}
              />
            </Grid>
            }

            {activeStep === 2 && (
              <Modules
                modules={modules.length > 0 ? modules : [{ name: "", resources: [] }]}
                setModules={setModules}
              />
            )}

            {/* Next and Previous buttons */}
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="space-between">
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                >
                  <Icon>arrow_back</Icon> &nbsp;Back
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleNext}
                  disabled={activeStep === 2}
                >
                  Next &nbsp;<Icon>arrow_forward</Icon>
                </MDButton>
              </MDBox>
            </Grid>

            {/* Submit Course Button */}
            <Grid item xs={12} justifyContent={"flex-end"} display="flex">
              {editCourse && (
                <MDButton
                  variant="outlined"
                  color="error"
                  onClick={handleCancelUpdate}
                  style={{ marginRight: "10px" }}
                >
                  <Icon>close</Icon> &nbsp;&nbsp;Cancel Update
                </MDButton>
              )}
              <MDButton variant="gradient" color="success" onClick={submitHandler} disabled={(!editCourse && activeStep !== 2) || !modules.some(m => m.resources && m.resources.length > 0)}
              >
                <Icon>save</Icon> &nbsp;&nbsp;
                {editCourse ? "Update Course" : "Add Course"}
              </MDButton>
            </Grid>
          </Grid>

        </MDBox>
        {notification.value && (
          <Snackbar open>
            <Alert variant="filled" sx={{ width: "100%" }} color={notification.color}>
              {notification.message}
            </Alert>
          </Snackbar>
        )}
        <MDBox mt={3} />
      </DashboardLayout>
    </>
  );
};

export default CourseForm;
